import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const useAuth = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    
    // ✅ Récupérer user_id APRÈS les hooks
    const user_id = localStorage.getItem('userId');

    // Fonction pour décoder le token JWT
    const decodeToken = (token) => {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) {
                throw new Error('Token JWT invalide');
            }
            const payload = parts[1];
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const decodedPayload = atob(base64);
            const parsedPayload = JSON.parse(decodedPayload);
            return parsedPayload;
        } catch (error) {
            console.error('Erreur lors du décodage du token:', error);
            return null;
        }
    };

    // Fonction pour obtenir le temps restant avant expiration du token
    const getTokenExpirationTime = (token) => {
        const decoded = decodeToken(token);
        if (!decoded || !decoded.exp) {
            return null;
        }
        const expirationTime = decoded.exp * 1000;
        const currentTime = Date.now();
        const timeRemaining = expirationTime - currentTime;
        console.log(`Token expire dans ${Math.floor(timeRemaining / 60000)} minutes`);
        return timeRemaining;
    };

    // Fonction pour rafraîchir le token
    const refreshAccessToken = async () => {
        try {
            const refresh = localStorage.getItem('refresh_token');

            if (!refresh) {
                console.log('Aucun refresh token trouvé');
                return false;
            }

            console.log('Tentative de rafraîchissement du token...');

            const response = await fetch(`${process.env.REACT_APP_HOTE}/api/customize/refresh-token/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ refresh })
            });

            const data = await response.json();

            if (response.ok && data.success && data.access) {
                localStorage.setItem("access_token", data.access);
                console.log('✅ Token rafraîchi avec succès à', new Date().toLocaleTimeString());

                if (data.access_token_expires_at) {
                    localStorage.setItem("access_token_expires_at", data.access_token_expires_at);
                }

                return true;
            } else {
                console.error('Échec du rafraîchissement:', data.error || data);
                return false;
            }
        } catch (error) {
            console.error('Erreur lors du rafraîchissement du token:', error);
            return false;
        }
    };

    // ✅ Fonction pour configurer le timeout de rafraîchissement avec vérification améliorée
    const setupRefreshTimeout = async () => {
        const token = localStorage.getItem('access_token');

        if (!token) {
            console.log('Token absent, impossible de configurer le refresh');
            return null;
        }

        const timeRemaining = getTokenExpirationTime(token);

        if (timeRemaining === null) {
            console.log('Impossible de déterminer l\'expiration du token');
            return null;
        }

        // ✅ Ne pas refresher si le token a plus de 10 minutes de vie
        if (timeRemaining > 10 * 60 * 1000) {
            console.log(`Token valide pour ${Math.floor(timeRemaining / 60000)} minutes, pas de refresh nécessaire`);
            return null;
        }

        if (timeRemaining <= 0) {
            console.log('Token déjà expiré, tentative de refresh immédiat...');
            const success = await refreshAccessToken();
            if (success) {
                return setupRefreshTimeout();
            }
            return null;
        }

        // ✅ Refresh 1 minute avant expiration (au lieu de 5)
        const refreshDelay = Math.max(timeRemaining - 60 * 1000, 0);
        const refreshDelayMinutes = Math.floor(refreshDelay / 60000);
        console.log(`⏰ Prochain rafraîchissement programmé dans ${refreshDelayMinutes} minutes`);

        const timeoutId = setTimeout(async () => {
            console.log('🔄 Lancement du rafraîchissement programmé...');
            const success = await refreshAccessToken();
            if (success) {
                setupRefreshTimeout();
            } else {
                console.log('❌ Échec du rafraîchissement programmé');
            }
        }, refreshDelay);

        return timeoutId;
    };

    // Fonction de déconnexion
    const handleLogout = () => {
        console.log('🔓 Déconnexion manuelle...');
        
        const keysToRemove = [
            'access_token', 'refresh_token', 'access_token_expires_at',
            'userId', 'groupe_id', 'accesGroupe',
            'user_nom', 'user_prenoms', 'user_email', 'user_telephone', 'user_type'
        ];
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        navigate('/', { replace: true });
    };

    // Fonction pour récupérer les informations de l'utilisateur
    const fetchUtilisateur = async () => {
        try {
            const token = localStorage.getItem('access_token');
            
            // ✅ Vérifier que user_id n'est pas null ou "null"
            const cleanUserId = user_id && user_id !== 'null' ? user_id : null;
            
            if (!token || !cleanUserId) {
                console.log('Token ou userId manquant:', { token: !!token, userId: cleanUserId });
                setIsLoading(false);
                return;
            }

            console.log('📡 Récupération infos utilisateur...');

            const response = await fetch(`${process.env.REACT_APP_HOTE}/api/recup-info-profil/${cleanUserId}/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });

            if (response.status === 404) {
                console.log('❌ Utilisateur non trouvé');
                setIsLoading(false);
                return;
            }

            if (response.status === 401) {
                console.log('🔄 Token invalide, tentative de rafraîchissement...');
                const refreshed = await refreshAccessToken();
                if (refreshed) {
                    return fetchUtilisateur();
                } else {
                    console.log('❌ Refresh échoué');
                    setIsLoading(false);
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
            }

            const data = await response.json();
            console.log('✅ Infos utilisateur récupérées');
            setFormData(data);
        } catch (error) {
            console.error('Erreur fetchUtilisateur:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Premier useEffect - Chargement des infos utilisateur
    useEffect(() => {
        const cleanUserId = user_id && user_id !== 'null' ? user_id : null;
        if (cleanUserId) {
            fetchUtilisateur();
        } else {
            console.log('Pas de userId valide, skip fetchUtilisateur');
            setIsLoading(false);
        }
    }, [user_id]);

    // ✅ Deuxième useEffect - Vérification du token (version améliorée sans déconnexion automatique)
    useEffect(() => {
        if (isLoading) return;

        const token = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');
        const cleanUserId = user_id && user_id !== 'null' ? user_id : null;

        console.log('🔍 Vérification auth - Token:', !!token);
        console.log('🔍 Vérification auth - Refresh Token:', !!refreshToken);
        console.log('🔍 Vérification auth - UserId:', cleanUserId);
        console.log('🔍 Vérification auth - isLoading:', isLoading);

        // ✅ Si pas de token mais pas de userId non plus, on est probablement sur la page login
        if (!token && !cleanUserId) {
            console.log('ℹ️ Pas de session, accès public');
            return;
        }

        // ✅ Si pas de token mais refresh token existe, essayer de refresher
        if (!token && refreshToken) {
            console.log('🔄 Token absent mais refresh présent, tentative de restauration...');
            refreshAccessToken().then(success => {
                if (!success) {
                    console.log('❌ Impossible de restaurer la session');
                }
            });
            return;
        }

        // ✅ Si token présent, vérifier son expiration
        if (token) {
            const timeRemaining = getTokenExpirationTime(token);
            
            if (timeRemaining !== null && timeRemaining <= 0) {
                console.log('⏰ Token expiré, tentative de rafraîchissement...');
                refreshAccessToken().then(success => {
                    if (!success) {
                        console.log('❌ Refresh échoué, session expirée');
                        // ✅ On ne déconnecte pas immédiatement, on laisse le temps de se reconnecter
                    }
                });
            } else if (timeRemaining !== null && timeRemaining < 10 * 60 * 1000) {
                console.log(`⚠️ Token bientôt expiré (${Math.floor(timeRemaining / 60000)} min restantes)`);
                // Programmer un refresh
                setupRefreshTimeout();
            }
        }

        // ✅ Vérification du statut du compte (seulement si formData est chargé)
        if (formData.statut && formData.statut !== 'actif' && formData.statut !== 'Actif') {
            console.log('⚠️ Compte non actif:', formData.statut);
            // ✅ On ne déconnecte pas automatiquement, on affiche juste un warning
            // handleLogout(); ← COMMENTÉ POUR ÉVITER LA DÉCONNEXION AUTOMATIQUE
        }

        // Configurer le timeout de rafraîchissement
        let timeoutId;
        setupRefreshTimeout().then(id => {
            timeoutId = id;
        });

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [formData, isLoading, user_id]);

    return { formData, isLoading, handleLogout, refreshAccessToken };
};

export default useAuth;